import React, { useState, useEffect } from "react";
import { Avatar, Table, Sheet, CircularProgress, Box, Typography } from "@mui/joy";
import { format, differenceInMinutes, parse } from "date-fns";

import { supabase } from "../../supabaseClient";
import { fetchProfilePicture, formatStudentName, formatDuration, getLocalDateString } from "../../utils";
import { Student } from "../../types";
import { CheckinCheckoutRecordView, ActionType } from "../../types/CheckinCheckoutRecord";
import { RelationshipType, RelationshipTypeLabels } from "../../types/ParentGuardian";
import SignatureModal from "../modals/SignatureModal";
import { usePagination } from "../../hooks/usePagination";
import Pagination from "../Pagination";

const STUDENT_PROFILE_PICTURES = "students";

interface AttendanceRecord {
  date: string;
  checkin: CheckinCheckoutRecordView | null;
  checkout: CheckinCheckoutRecordView | null;
}

interface StudentAttendanceTableProps {
  student?: Student;
}

const StudentAttendanceTable: React.FC<StudentAttendanceTableProps> = ({ student }) => {
  const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [signatureModal, setSignatureModal] = useState<{
    open: boolean;
    signatureData: string;
    action: ActionType;
    timestamp: string;
    parentName: string;
  }>({
    open: false,
    signatureData: '',
    action: 'checkin',
    timestamp: '',
    parentName: '',
  });

  // Use pagination hook
  const {
    paginatedData: paginatedRecords,
    currentPage,
    totalPages,
    totalItems,
    rowsPerPage,
    setCurrentPage,
    setRowsPerPage,
  } = usePagination({
    data: attendanceRecords,
    searchTerm: "", // No search for attendance table
    searchFields: [],
  });

  const fetchAttendanceRecords = async () => {
    setLoading(true);
    try {
      const query = supabase
        .from("checkin_checkout_records_view")
        .select("*")
        .order("created_at", { ascending: false });

      if (student) {
        query.eq("student_id", student.id);
      }

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      const records = await data.reduce(async (accPromise, record) => {
        const acc = await accPromise;
        const date = getLocalDateString(record.created_at);

        if (!acc[date]) {
          acc[date] = { date, checkin: null, checkout: null };
        }

        const formattedRecord: CheckinCheckoutRecordView = {
          id: record.id,
          action: record.action,
          signature: record.signature || '',
          created_at: record.created_at,
          class: {
            id: record.class_id,
            schedule: record.schedule,
            start_month: record.start_month,
            start_year: record.start_year,
            end_month: record.end_month,
            end_year: record.end_year,
          },
          student: {
            id: record.student_id,
            first_name: record.student_first_name,
            last_name: record.student_last_name,
            preferred_first_name: record.student_preferred_first_name,
            profile_picture: await fetchProfilePicture(STUDENT_PROFILE_PICTURES, record.student_profile_picture_id),
            date_of_birth: record.student_date_of_birth || null,
          },
          parent_guardian: {
            id: record.parent_guardian_id,
            first_name: record.parent_guardian_first_name,
            last_name: record.parent_guardian_last_name,
            relationship: record.parent_guardian_relationship,
          },
          teachers: {
            primary: record.primary_teacher_first_name ? {
              first_name: record.primary_teacher_first_name,
              last_name: record.primary_teacher_last_name,
            } : undefined,
            secondary: record.secondary_teacher_first_name ? {
              first_name: record.secondary_teacher_first_name,
              last_name: record.secondary_teacher_last_name,
            } : undefined,
          },
        };

        if (record.action === "checkin" && !acc[date].checkin) {
          acc[date].checkin = formattedRecord;
        } else if (record.action === "checkout" && !acc[date].checkout) {
          acc[date].checkout = formattedRecord;
        }

        return acc;
      }, Promise.resolve({} as Record<string, AttendanceRecord>));

      setAttendanceRecords(Object.values(records));
    } catch (error) {
      console.error("Error fetching attendance records:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttendanceRecords();
    /* eslint-disable-next-line */
  }, []);

  const handleViewSignature = (record: CheckinCheckoutRecordView) => {
    setSignatureModal({
      open: true,
      signatureData: record.signature,
      action: record.action,
      timestamp: record.created_at,
      parentName: `${record.parent_guardian.first_name} ${record.parent_guardian.last_name}`,
    });
  };

  const formatScheduleForDay = (schedule: any, day: string) => {
    if (!schedule) return "No Schedule";
    
    const daySchedule = schedule[day.toLowerCase()];
    if (!daySchedule) return "No Schedule";

    if (daySchedule.hasSchool && daySchedule.start && daySchedule.end) {
      const start = format(new Date(`1970-01-01T${daySchedule.start}`), "h:mm a");
      const end = format(new Date(`1970-01-01T${daySchedule.end}`), "h:mm a");
      return `${day.charAt(0).toUpperCase() + day.slice(1)}: ${start} - ${end}`;
    }
    return "No Schedule";
  };

  return (
    <Sheet variant="outlined" sx={{ width: "100%", borderRadius: "sm", overflow: "auto", minHeight: 0 }}>
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      ) : paginatedRecords.length > 0 ? (
        <>
          <Table>
            <thead>
              <tr>
                <th style={{ minWidth: 120 }}>Date</th>
                {!student?.id && (
                  <th>Student Name</th>
                )}
                <th style={{ minWidth: 400 }}>Checkin</th>
                <th style={{ minWidth: 600 }}>Checkout</th>
                <th style={{ minWidth: 150 }}>Time in Class</th>
                <th style={{ minWidth: 600 }}>Class Schedule</th>
                <th style={{ minWidth: 200 }}>Class Teachers</th>
              </tr>
            </thead>
            <tbody>
              {paginatedRecords.map((record) => {
              const { date, checkin, checkout } = record;
              // Parse the date string (YYYY-MM-DD) as local date using date-fns
              const localDate = parse(date, 'yyyy-MM-dd', new Date());
              const dayOfWeek = localDate.toLocaleDateString("en-US", { weekday: "long" });
              const schedule = checkin?.class.schedule || checkout?.class.schedule;

              return (
                <tr key={date}>
                  <td>
                    <Typography>{format(localDate, "MM/dd/yyyy")}</Typography>
                  </td>
                  {!student?.id && (
                    <td>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar src={checkin?.student.profile_picture || undefined} sx={{ width: 32, height: 32 }}>
                          {!checkin?.student.profile_picture && checkin?.student && 
                            `${checkin.student.first_name[0]}${checkin.student.last_name[0]}`}
                        </Avatar>
                        <Typography>
                          {checkin?.student && formatStudentName(checkin.student)}
                        </Typography>
                      </Box>
                    </td>
                  )}
                  <td>
                    {checkin ? (
                      <Box>
                        <Typography>
                          {format(new Date(checkin.created_at), "h:mm a")} by{" "}
                          {checkin.parent_guardian.first_name} {checkin.parent_guardian.last_name} (
                          {RelationshipTypeLabels[checkin.parent_guardian.relationship as RelationshipType]})
                          {" "}<Typography 
                            component="span" 
                            sx={{ 
                              color: 'primary.500',
                              cursor: 'pointer',
                              '&:hover': { textDecoration: 'underline' }
                            }}
                            onClick={() => handleViewSignature(checkin)}
                          >
                            (Signature)
                          </Typography>
                        </Typography>
                      </Box>
                    ) : (
                      <Typography>{"\u2014"}</Typography>
                    )}
                  </td>
                  <td>
                    {checkout ? (
                      <Box>
                        <Typography>
                          {format(new Date(checkout.created_at), "h:mm a")} by{" "}
                          {checkout.parent_guardian.first_name} {checkout.parent_guardian.last_name} (
                          {RelationshipTypeLabels[checkout.parent_guardian.relationship as RelationshipType]})
                          {" "}<Typography 
                            component="span" 
                            sx={{ 
                              color: 'primary.500',
                              cursor: 'pointer',
                              '&:hover': { textDecoration: 'underline' }
                            }}
                            onClick={() => handleViewSignature(checkout)}
                          >
                            (Signature)
                          </Typography>
                        </Typography>
                      </Box>
                    ) : (
                      <Typography>{"\u2014"}</Typography>
                    )}
                  </td>
                  <td>
                    <Typography>
                      {(() => {
                        if (checkin && checkout) {
                          const minutes = differenceInMinutes(
                            new Date(checkout.created_at),
                            new Date(checkin.created_at)
                          );
                          return formatDuration(minutes);
                        }
                        return "—";
                      })()}
                    </Typography>
                  </td>
                  <td>
                    <Typography>
                      {schedule ? formatScheduleForDay(schedule, dayOfWeek) : "No Schedule for Date"}
                    </Typography>
                  </td>
                  <td>
                    <Typography>
                      {checkin?.teachers.primary &&
                        `${checkin.teachers.primary.first_name} ${checkin.teachers.primary.last_name}`}
                      {checkin?.teachers.secondary &&
                        `, ${checkin.teachers.secondary.first_name} ${checkin.teachers.secondary.last_name}`}
                    </Typography>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        {paginatedRecords.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            rowsPerPage={rowsPerPage}
            onPageChange={setCurrentPage}
            onRowsPerPageChange={setRowsPerPage}
          />
        )}
        </>
      ) : (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="200px">
          <Typography level="body-lg" color="neutral">
            No Attendance Records
          </Typography>
        </Box>
      )}
      <SignatureModal
        open={signatureModal.open}
        onClose={() => setSignatureModal(prev => ({ ...prev, open: false }))}
        signatureData={signatureModal.signatureData}
        action={signatureModal.action}
        timestamp={signatureModal.timestamp}
        parentName={signatureModal.parentName}
      />
    </Sheet>
  );
};

export default StudentAttendanceTable;
